'use client';

import { useEffect, useState } from 'react';
import { Search, Plus, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import { adminApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';

interface Provider {
  id: string;
  name: string;
  displayName: string;
  integrationType: 'http_api' | 'rest_api' | 'smpp' | 'sip' | 'webhook' | 'csv_import' | 'manual_pool';
  operatingMode: 'api_only' | 'manual_only' | 'hybrid';
  config: Record<string, unknown>;
  credentials: Record<string, string>;
  routingPriority: number;
  routingWeight: number;
  healthStatus: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  lastHealthCheck: string | null;
  circuitBreakerTrips: number;
  monthlyCostLimit: number;
  currentMonthCost: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateProviderData {
  name: string;
  displayName: string;
  integrationType: string;
  operatingMode: string;
  config: Record<string, unknown>;
  credentials: Record<string, string>;
  routingPriority: number;
  routingWeight: number;
  monthlyCostLimit: number;
}

export function AdminProvidersPage() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeProviderTab, setActiveProviderTab] = useState<string>('basic');
  const [creating, setCreating] = useState(false);
  const [newProvider, setNewProvider] = useState<CreateProviderData>({
    name: '',
    displayName: '',
    integrationType: 'http_api',
    operatingMode: 'api_only',
    config: {},
    credentials: {},
    routingPriority: 100,
    routingWeight: 1,
    monthlyCostLimit: 1000,
  });

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getProviders() as Provider[] | null;
      if (response) {
        setProviders(response);
      }
    } catch (err) {
      toast.error('Failed to fetch providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleCreate = async () => {
    if (!newProvider.name || !newProvider.displayName) {
      toast.error('Name and display name are required');
      return;
    }
    setCreating(true);
    try {
      const response = await adminApi.createProvider(newProvider);
      if (response) {
        toast.success('Provider created');
        setCreateDialogOpen(false);
        setNewProvider({
          name: '',
          displayName: '',
          integrationType: 'http_api',
          operatingMode: 'api_only',
          config: {},
          credentials: {},
          routingPriority: 100,
          routingWeight: 1,
          monthlyCostLimit: 1000,
        });
        fetchProviders();
      } else {
        toast.error('Failed to create provider');
      }
    } catch (err) {
      toast.error('Failed to create provider');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this provider?')) return;
    try {
      await adminApi.deleteProvider(id);
      toast.success('Provider deleted');
      fetchProviders();
    } catch (err) {
      toast.error('Failed to delete provider');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'healthy': return <Badge className="bg-green-500/10 text-green-400">Healthy</Badge>;
      case 'degraded': return <Badge className="bg-amber-500/10 text-amber-400">Degraded</Badge>;
      case 'unhealthy': return <Badge className="bg-red-500/10 text-red-400">Unhealthy</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      http_api: 'bg-cyan-500/10 text-cyan-400',
      rest_api: 'bg-blue-500/10 text-blue-400',
      smpp: 'bg-purple-500/10 text-purple-400',
      sip: 'bg-pink-500/10 text-pink-400',
      webhook: 'bg-green-500/10 text-green-400',
      csv_import: 'bg-amber-500/10 text-amber-400',
      manual_pool: 'bg-slate-500/10 text-slate-400',
    };
    return <Badge className={colors[type] || colors.manual_pool}>{type.replace('_', ' ').toUpperCase()}</Badge>;
  };

  const getModeBadge = (mode: string) => {
    const colors: Record<string, string> = {
      api_only: 'bg-blue-500/10 text-blue-400',
      manual_only: 'bg-amber-500/10 text-amber-400',
      hybrid: 'bg-purple-500/10 text-purple-400',
    };
    return <Badge className={colors[mode] || colors.api_only}>{mode.replace('_', ' ')}</Badge>;
  };

  const filteredProviders = providers.filter(p => {
    if (search && !p.displayName.toLowerCase().includes(search.toLowerCase()) && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && p.healthStatus !== statusFilter) return false;
    if (typeFilter && p.integrationType !== typeFilter) return false;
    if (modeFilter && p.operatingMode !== modeFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="glass">
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Provider Management</h1>
          <p className="text-slate-400 mt-1">Manage telecom providers and integrations</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Provider
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Provider</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Tabs value={activeProviderTab} onValueChange={(v) => setActiveProviderTab(v)} className="space-y-4">
                <TabsList>
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="config">Configuration</TabsTrigger>
                  <TabsTrigger value="credentials">Credentials</TabsTrigger>
                  <TabsTrigger value="routing">Routing</TabsTrigger>
                </TabsList>
                <TabsContent value="basic">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Name (internal)</label>
                      <Input
                        value={newProvider.name}
                        onChange={e => setNewProvider({ ...newProvider, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                        placeholder="provider_name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Display Name</label>
                      <Input
                        value={newProvider.displayName}
                        onChange={e => setNewProvider({ ...newProvider, displayName: e.target.value })}
                        placeholder="Provider Display Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Integration Type</label>
                      <Select
                        value={newProvider.integrationType}
                        onValueChange={v => setNewProvider({ ...newProvider, integrationType: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="http_api">HTTP API</SelectItem>
                          <SelectItem value="rest_api">REST API</SelectItem>
                          <SelectItem value="smpp">SMPP</SelectItem>
                          <SelectItem value="sip">SIP</SelectItem>
                          <SelectItem value="webhook">Webhook</SelectItem>
                          <SelectItem value="csv_import">CSV Import</SelectItem>
                          <SelectItem value="manual_pool">Manual Pool</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Operating Mode</label>
                      <Select
                        value={newProvider.operatingMode}
                        onValueChange={v => setNewProvider({ ...newProvider, operatingMode: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="api_only">API Only</SelectItem>
                          <SelectItem value="manual_only">Manual Only</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="config">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Configuration (JSON)</label>
                      <textarea
                        className="w-full h-64 bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white font-mono text-sm"
                        value={JSON.stringify(newProvider.config, null, 2)}
                        onChange={e => {
                          try {
                            setNewProvider({ ...newProvider, config: JSON.parse(e.target.value) });
                          } catch {
                            // ignore invalid JSON
                          }
                        }}
                        placeholder='{"baseUrl": "https://api.provider.com", "endpoints": {...}}'
                      />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="credentials">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Credentials (JSON)</label>
                      <textarea
                        className="w-full h-64 bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white font-mono text-sm"
                        value={JSON.stringify(newProvider.credentials, null, 2)}
                        onChange={e => {
                          try {
                            setNewProvider({ ...newProvider, credentials: JSON.parse(e.target.value) });
                          } catch {
                            // ignore invalid JSON
                          }
                        }}
                        placeholder='{"apiKey": "xxx", "secret": "yyy"}'
                      />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="routing">
                  <div className="space-y-4 grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Routing Priority</label>
                      <Input
                        type="number"
                        value={newProvider.routingPriority}
                        onChange={e => setNewProvider({ ...newProvider, routingPriority: parseInt(e.target.value) })}
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Routing Weight</label>
                      <Input
                        type="number"
                        value={newProvider.routingWeight}
                        onChange={e => setNewProvider({ ...newProvider, routingWeight: parseInt(e.target.value) })}
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Monthly Cost Limit ($)</label>
                      <Input
                        type="number"
                        value={newProvider.monthlyCostLimit}
                        onChange={e => setNewProvider({ ...newProvider, monthlyCostLimit: parseFloat(e.target.value) })}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Create Provider'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="glass">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>All Providers</CardTitle>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  placeholder="Search providers..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Status</SelectItem>
                  <SelectItem value="healthy">Healthy</SelectItem>
                  <SelectItem value="degraded">Degraded</SelectItem>
                  <SelectItem value="unhealthy">Unhealthy</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Types</SelectItem>
                  <SelectItem value="http_api">HTTP API</SelectItem>
                  <SelectItem value="rest_api">REST API</SelectItem>
                  <SelectItem value="smpp">SMPP</SelectItem>
                  <SelectItem value="sip">SIP</SelectItem>
                  <SelectItem value="webhook">Webhook</SelectItem>
                  <SelectItem value="csv_import">CSV Import</SelectItem>
                  <SelectItem value="manual_pool">Manual Pool</SelectItem>
                </SelectContent>
              </Select>
              <Select value={modeFilter} onValueChange={(v) => setModeFilter(v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Modes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Modes</SelectItem>
                  <SelectItem value="api_only">API Only</SelectItem>
                  <SelectItem value="manual_only">Manual Only</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Provider</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Weight</TableHead>
                <TableHead>Cost Limit</TableHead>
                <TableHead>Current Cost</TableHead>
                <TableHead>Circuit Breaker</TableHead>
                <TableHead>Last Check</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProviders.length > 0 ? (
                filteredProviders.map(provider => (
                  <TableRow key={provider.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">{provider.displayName}</p>
                        <p className="text-xs text-slate-400 font-mono">{provider.name}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(provider.integrationType)}</TableCell>
                    <TableCell>{getModeBadge(provider.operatingMode)}</TableCell>
                    <TableCell>{getStatusBadge(provider.healthStatus)}</TableCell>
                    <TableCell>{provider.routingPriority}</TableCell>
                    <TableCell>{provider.routingWeight}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(provider.monthlyCostLimit)}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(provider.currentMonthCost)}</TableCell>
                    <TableCell className={cn(provider.circuitBreakerTrips > 0 && 'text-red-400')}>
                      {provider.circuitBreakerTrips}
                    </TableCell>
                    <TableCell className="text-slate-400">
                      {provider.lastHealthCheck ? formatDateTime(provider.lastHealthCheck) : 'Never'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="text-cyan-400 hover:text-cyan-300">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-amber-400 hover:text-amber-300" onClick={() => handleDelete(provider.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-slate-400">No providers found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
